import { LineTrashAlt } from '@qr-front/icons/line-trash-alt'
import { Button } from '@qr-front/ui/components/Button'
import { Spinner } from '@qr-front/ui/components/Spinner'
import {
	DESCRIPTION_MAX_LENGTH,
	NAME_MAX_LENGTH,
	URL_MAX_LENGTH,
} from 'constants/validationConstants'
import { RhcField } from 'features/generator/components/RhcField'
import { useAtom, useAtomValue } from 'jotai'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import { useBus, useListener } from 'react-bus'
import { useFormContext } from 'react-hook-form'
import { TUploadedMedia } from 'services/mediaStorage'
import { maxLength } from 'utils/inputChangeUtils'

import { InputButton } from '../InputButton'
import {
	CUSTOM_SOCIAL_IMAGE_UPLOAD_FINISHED,
	CUSTOM_SOCIAL_IMAGE_UPLOAD_STARTED,
	customSocialImageAtom,
	CustomSocialImageUpload,
} from './CustomSocialImageUpload'
import { socialLinkListAtom } from './SocialLinksForm'
import {
	TSocialLinkField,
	TSocialLinksFormFields,
} from './socialLinksFormSchema'

export const generateCustomPlatformId = (): string => crypto.randomUUID()

export const createEmptyCustomSocial =
	(): TSocialLinksFormFields['customSocialLink'] => ({
		url: '',
		name: '',
		description: '',
		platformId: generateCustomPlatformId(),
		logo: {
			src: '/qr-code-generator/social-networks/custom-social.svg',
			provider: 'none',
		},
	})

export const CustomSocialLinkFields = ({
	setIsAddingCustomSocial,
}: {
	setIsAddingCustomSocial: (value: boolean) => void
}) => {
	const t = useTranslations()
	const bus = useBus()
	const [isLoading, setIsLoading] = useState(false)

	const [socialLinkList, setSocialLinkList] =
		useAtom<TSocialLinkField[]>(socialLinkListAtom)
	const customUploadedImage = useAtomValue<TUploadedMedia | null>(
		customSocialImageAtom,
	)

	const form = useFormContext<TSocialLinksFormFields>()
	const {
		formState: { errors },
		getValues,
		setValue,
		trigger,
		clearErrors,
	} = form

	const formValues = getValues('customSocialLink')

	const handleClickAdd = async () => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur()
		}

		const isValid = await trigger('customSocialLink')
		if (!isValid) return

		const newCustomSocialLink = getValues('customSocialLink')

		const modifiedCustomUploadedImage = {
			bucketRootUrl: customUploadedImage?.bucketRootUrl,
			bytes: customUploadedImage?.bytes,
			format: customUploadedImage?.format,
			provider: customUploadedImage?.storageProvider,
			publicId: customUploadedImage?.publicId,
			resourceType: customUploadedImage?.resourceType,
		}

		const modifiedCustomSocialLink = {
			...newCustomSocialLink,
			logo: customUploadedImage?.publicId
				? modifiedCustomUploadedImage
				: newCustomSocialLink.logo,
		}

		const newList = [
			...socialLinkList,
			modifiedCustomSocialLink,
		] as TSocialLinksFormFields['socialLinks']

		setValue('socialLinks', newList)
		setSocialLinkList(newList)

		await trigger('socialLinks')

		setValue('customSocialLink', createEmptyCustomSocial())
		setIsAddingCustomSocial(false)
	}

	const handleClickDelete = () => {
		setValue('customSocialLink', createEmptyCustomSocial())
		setIsAddingCustomSocial(false)
		clearErrors('customSocialLink')
		bus.emit(CUSTOM_SOCIAL_IMAGE_UPLOAD_FINISHED)
	}

	useListener(CUSTOM_SOCIAL_IMAGE_UPLOAD_STARTED, () => setIsLoading(true))
	useListener(CUSTOM_SOCIAL_IMAGE_UPLOAD_FINISHED, () => setIsLoading(false))

	return (
		<div className="mt-8 flex w-full flex-col items-start justify-between gap-4 rounded-10 bg-light-grey-70 p-4 desktop:p-6">
			<div className="flex w-full flex-col gap-4 desktop:gap-6">
				<div className="mb-2 tablet:mb-0">
					<CustomSocialImageUpload />
				</div>
				<div className="flex w-full flex-col gap-4 tablet:flex-row">
					<div className="w-full grow desktop:grow-0">
						<RhcField
							form={form}
							path="customSocialLink.name"
							value={formValues.name}
							setValue={(val) =>
								setValue('customSocialLink.name', val ?? '')
							}
							fieldName="custom_social_link_name"
							fieldGroup="custom_social_link"
							type="text"
							label={t(
								'generator__content_form_section__social__input__name_label',
							)}
							placeholder={t(
								'generator__content_form_section__social__input__name_placeholder',
							)}
							error={errors.customSocialLink?.name?.message}
							onChangeTransform={(e) =>
								maxLength(NAME_MAX_LENGTH + 1)(e)
							}
						/>
					</div>

					<div className="w-full grow desktop:grow-0">
						<RhcField
							form={form}
							path="customSocialLink.url"
							value={formValues.url}
							setValue={(val) =>
								setValue('customSocialLink.url', val ?? '')
							}
							fieldName="custom_social_link_url"
							fieldGroup="custom_social_link"
							type="url"
							label={t(
								'generator__content_form_section__social__input__url_label',
							)}
							placeholder={t(
								'generator__content_form_section__social__input__url_placeholder',
							)}
							error={errors.customSocialLink?.url?.message}
							onChangeTransform={(e) =>
								maxLength(URL_MAX_LENGTH + 1)(e)
							}
						/>
					</div>
				</div>

				<div className="flex flex-col gap-4 tablet:flex-row tablet:items-end desktop:-mt-2">
					<div className="grow desktop:w-[37.313rem] desktop:grow-0">
						<RhcField
							form={form}
							path="customSocialLink.description"
							value={formValues.description}
							setValue={(val) =>
								setValue(
									'customSocialLink.description',
									val ?? '',
								)
							}
							fieldName="custom_social_link_description"
							fieldGroup="custom_social_link"
							type="text"
							label={t(
								'generator__content_form_section__social__input__description_label',
							)}
							placeholder={t(
								'generator__content_form_section__social__input__description_placeholder',
							)}
							onChangeTransform={(e) =>
								maxLength(DESCRIPTION_MAX_LENGTH)(e)
							}
							error={
								errors.customSocialLink?.description?.message
							}
						/>
					</div>

					<div className="flex gap-4">
						<Button
							visual="default"
							size="large"
							onClick={handleClickAdd}
							type="button"
							className="grow"
							disabled={isLoading}
						>
							{t(
								'generator__content_form_section__social__add__button',
							)}
							{isLoading ? <Spinner color="white" /> : null}
						</Button>
						<InputButton
							icon={<LineTrashAlt className="size-5" />}
							onClick={handleClickDelete}
							className={
								isLoading
									? 'pointer-events-none opacity-50'
									: undefined
							}
						/>
					</div>
				</div>
			</div>
		</div>
	)
}
